import { AllocationDetails } from './AllocationEditor';
import { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import PaymentForm from './PaymentForm';

const STATUS_LABELS = { new: 'Новая', pending: 'На подписи', paid: 'Оплачена', rejected: 'Отклонена' };
const ALLOWED_TRANSITIONS = { accountant: ['pending'], admin: ['paid', 'rejected', 'new', 'pending'] };

export default function PaymentDetail({ id, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [showPaidBox, setShowPaidBox] = useState(false);
  // По умолчанию — сегодня, а не автоматический NOW() на сервере: тот момент клика в
  // интерфейсе часто не совпадает с реальной датой перевода денег (отметили на день-два позже),
  // а от этой даты напрямую зависит дата записи в ДДС.
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [editing, setEditing] = useState(false);

  const load = () => {
    api.get(`/payments/${id}`).then(r => setData(r.data)).catch(() => setError('Не удалось загрузить заявку'));
  };

  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status, comment, datePaid) => {
    setBusy(true);
    setError('');
    try {
      await api.patch(`/payments/${id}/status`, { status, comment, date_paid: datePaid });
      load();
      onChanged();
      setShowRejectBox(false);
      setRejectComment('');
      setShowPaidBox(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Удалить заявку безвозвратно?')) return;
    setBusy(true);
    try {
      await api.delete(`/payments/${id}`);
      onChanged();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления');
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal"><div className="error-msg">{error}</div><button className="btn-secondary" onClick={onClose}>Закрыть</button></div>
    </div>
  );
  if (!data) return (
    <div className="modal-overlay"><div className="modal">Загрузка...</div></div>
  );

  const allowedTargets = (ALLOWED_TRANSITIONS[user.role] || []).filter(s => s !== data.status);
  const canEdit = user.role === 'admin' || data.creator_id === user.id;

  if (editing) {
    return (
      <PaymentForm
        initial={data}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); load(); onChanged(); }}
      />
    );
  }

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2>Заявка #{data.id} <span className={`badge badge-${data.status}`}>{STATUS_LABELS[data.status]}</span></h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="error-msg">{error}</div>}

        <div className="form-row">
          <div><label>Тип</label><div>{data.doc_type === 'fact' ? 'Оплата факт' : 'Заявка на оплату'}</div></div>
          <div><label>Тип оплаты</label><div>{data.pay_type}</div></div>
        </div>
        {data.pay_type === 'По номеру телефона' ? (
          <div className="form-row mt-2">
            <div><label>Телефон</label><div>{data.phone_number || '—'}</div></div>
            <div><label>Получатель перевода</label><div>{data.phone_recipient || '—'}</div></div>
            <div><label>Банк</label><div>{data.phone_bank || '—'}</div></div>
          </div>
        ) : data.contractor_requisites && (
          <div className="mt-2"><label>Реквизиты</label><div style={{ whiteSpace: 'pre-wrap' }}>{data.contractor_requisites}</div></div>
        )}
        <div className="form-row mt-2">
          <div><label>Направление</label><div>{data.direction}</div></div>
          <div><label>Статья</label><div>{data.article}</div></div>
        </div>
        <div className="form-row mt-2">
          <div><label>Проект</label><div>{data.project}</div></div>
          <div><label>Источник</label><div>{data.source}</div></div>
        </div>
        <div className="form-row mt-2">
          <div><label>Сумма</label><div>{parseFloat(data.amount).toLocaleString('ru')} ₽</div></div>
          <div><label>Получатель</label><div>{data.recipient || '—'}</div></div>
        </div>
        <div className="mt-2"><label>Комментарий</label><div>{data.comment || '—'}</div></div>
        <div className="mt-2"><label>Создатель</label><div>{data.creator_name || '—'}</div></div>

        {data.attachments?.length > 0 && (
          <div className="mt-2">
            <label>Вложения</label>
            {data.attachments.map(a => (
              <div key={a.id}>
                {a.kind === 'requisites' ? 'Реквизиты: ' : ''}
                <a href={a.url} target="_blank" rel="noreferrer">{a.filename || a.url}</a>
              </div>
            ))}
          </div>
        )}

        {allowedTargets.length > 0 && (
          <div className="mt-2" style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <label>Изменить статус</label>
            <div className="toolbar">
              {allowedTargets.map(s => (
                <button
                  key={s}
                  className="btn-secondary btn-sm"
                  disabled={busy}
                  onClick={() => s === 'rejected' ? setShowRejectBox(true) : s === 'paid' ? setShowPaidBox(true) : changeStatus(s)}
                >
                  → {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            {showRejectBox && (
              <div className="mt-2">
                <textarea rows={2} placeholder="Причина отклонения" value={rejectComment} onChange={e => setRejectComment(e.target.value)} />
                <div className="modal-actions">
                  <button className="btn-secondary btn-sm" onClick={() => setShowRejectBox(false)}>Отмена</button>
                  <button className="btn-danger btn-sm" disabled={busy} onClick={() => changeStatus('rejected', rejectComment)}>Отклонить</button>
                </div>
              </div>
            )}
            {showPaidBox && (
              <div className="mt-2">
                <label>Дата фактической оплаты</label>
                <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} />
                <div className="modal-actions">
                  <button className="btn-secondary btn-sm" onClick={() => setShowPaidBox(false)}>Отмена</button>
                  <button className="btn-primary btn-sm" disabled={busy || !paidDate} onClick={() => changeStatus('paid', undefined, paidDate)}>Подтвердить оплату</button>
                </div>
              </div>
            )}
          </div>
        )}

        <AllocationDetails rows={data.allocations}/>
        {data.logs?.length > 0 && (
          <div className="mt-2" style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <label>История</label>
            {data.logs.map(l => (
              <div key={l.id} className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>
                {new Date(l.created_at).toLocaleString('ru')} — {l.user_name || 'система'}: {l.action}
                {l.old_status && l.new_status ? ` (${STATUS_LABELS[l.old_status] || l.old_status} → ${STATUS_LABELS[l.new_status] || l.new_status})` : ''}
                {l.comment ? `, "${l.comment}"` : ''}
              </div>
            ))}
          </div>
        )}

        <div className="modal-actions">
          {user.role === 'admin' && <button className="btn-danger" disabled={busy} onClick={remove}>Удалить</button>}
          {canEdit && <button className="btn-secondary" disabled={busy} onClick={() => setEditing(true)}>Изменить</button>}
          <button className="btn-secondary" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
